import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, Calendar, Hash, Edit, Check, Share2, BarChart, Users, Plus, Trash2, X } from 'lucide-react';
import API_BASE_URL from '../config/api';

interface Test {
  id: number;
  title: string;
  total_questions: number;
  default_answers: number;
  created_at: string;
  updated_at: string;
  statut_id: number;
  type_id: number;
  statut_libelle: string;
  student_count: number;
}

const MyTests: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; testId: number | null; testTitle: string }>({
    show: false,
    testId: null,
    testTitle: ''
  });
  const [deleting, setDeleting] = useState<boolean>(false);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/my-tests`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('tests.myTests.fetchError'));
      }

      const data = await response.json();
      setTests(data.tests);
      setLoading(false);

    } catch (err) {
      console.error('Erreur:', err);
      setError(err instanceof Error ? err.message : t('common.error'));
      setLoading(false);
    }
  };

  const handleBack = (): void => {
    navigate('/my-tests');
  };

  const openDeleteModal = (testId: number, testTitle: string): void => {
    setDeleteModal({ show: true, testId, testTitle });
  };

  const closeDeleteModal = (): void => {
    setDeleteModal({ show: false, testId: null, testTitle: '' });
  };

  const handleDeleteTest = async (): Promise<void> => {
    if (!deleteModal.testId) return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    setDeleting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${deleteModal.testId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la suppression');
      }

      // Retirer le test de la liste
      setTests(prevTests => prevTests.filter(test => test.id !== deleteModal.testId));
      closeDeleteModal();

    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLaunchTest = async (testId: number): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${testId}/launch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('tests.myTests.launchError'));
      }

      const data = await response.json();

      // Mettre à jour le statut du test dans l'état local
      setTests(prevTests =>
        prevTests.map(test =>
          test.id === testId
            ? { ...test, statut_id: 3, statut_libelle: 'En cours' }
            : test
        )
      );

    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-xl">{t('tests.myTests.loadingTests')}</div>
      </div>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {tests.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {t('tests.myTests.noTests')}
              </h3>
              <p className="text-gray-500 mb-6">
                {t('tests.myTests.noTestsMessage')}
              </p>
              <button
                onClick={() => navigate('/create-test')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition"
              >
                {t('tests.myTests.createFirstTest')}
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">
                  {tests.length} {tests.length > 1 ? t('tests.myTests.testsCreatedPlural') : t('tests.myTests.testsCreated')}
                </h2>
                <button
                  onClick={() => navigate('/create-test')}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
                >
                  <Plus className="w-5 h-5" />
                  {t('tests.myTests.createTest')}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tests.map((test) => (
                  <div
                    key={test.id}
                    className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200 hover:shadow-lg transition relative flex flex-col"
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(test.id, test.title);
                      }}
                      className="absolute top-3 right-3 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition"
                      title="Supprimer"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>

                    <div className="mb-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <FileText className="text-white w-6 h-6" />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-800 mb-3 truncate">
                      {test.title}
                    </h3>

                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2">
                        <Hash className="w-4 h-4" />
                        <span>{test.total_questions} {t('tests.myTests.questions')}</span>
                      </div>
                      {(test.statut_id === 3 || test.statut_id === 4) && (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{test.student_count} {test.student_count > 1 ? t('tests.myTests.studentsPlural') : t('tests.myTests.students')} {test.student_count > 1 ? t('tests.myTests.answeredPlural') : t('tests.myTests.answered')}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(test.created_at)}</span>
                      </div>
                    </div>

                    <div className="mt-auto pt-4 space-y-2">
                      {test.statut_id === 2 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLaunchTest(test.id);
                          }}
                          className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                        >
                          <Check className="w-4 h-4" />
                          {t('tests.myTests.launchTest')}
                        </button>
                      )}

                      {test.statut_id === 3 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/share-test/${test.id}`);
                          }}
                          className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                        >
                          <Share2 className="w-4 h-4" />
                          {t('tests.myTests.share')}
                        </button>
                      )}

                      {(test.statut_id === 3 || test.statut_id === 4) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/test-results/${test.id}`);
                          }}
                          className="w-full flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                        >
                          <BarChart className="w-4 h-4" />
                          {t('tests.myTests.viewResults')}
                        </button>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (test.type_id === 2) {
                            navigate('/detailed-test-configuration', {
                              state: { testId: test.id, testTitle: test.title, mode: 'edit', parts: [], questions: [] }
                            });
                          } else {
                            navigate(`/answer-test/${test.id}`);
                          }
                        }}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                      >
                        <Edit className="w-4 h-4" />
                        {test.statut_id === 1 ? t('tests.myTests.enterAnswers') : t('tests.myTests.editAnswers')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

      {/* Modal de confirmation de suppression */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-bold text-gray-800">Confirmer la suppression</h3>
              <button
                onClick={closeDeleteModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Êtes-vous sûr de vouloir supprimer le test <strong>"{deleteModal.testTitle}"</strong> ?
              </p>
              <p className="text-red-600 text-sm mb-6">
                Cette action est irréversible.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={closeDeleteModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteTest}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                >
                  {deleting ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default MyTests;
